/**
 * CernerShell — Cerner PowerChart simulation.
 * - Today's schedule strip with 2 Cerner patients (clickable patient switcher)
 * - Full tab content per patient (Summary, Chart Review, Orders, Results, Notes, mPages)
 * - Persistent right RAAPID panel (collapsible)
 * - Live Cerner open FHIR data where available, dummy fallback
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAllFhirData, type FhirData } from '../services/cernerFhir';
import { MOCK_PROVIDERS, type MockProvider } from './data/mockProviders';
import {
  MARGARET, DOROTHY,
  CERNER_VITALS, CERNER_CONDITIONS, CERNER_MEDICATIONS, CERNER_LABS, CERNER_NOTES, CERNER_ORDERS, CERNER_CHART_DOCS,
  DOROTHY_VITALS, DOROTHY_CONDITIONS, DOROTHY_MEDICATIONS, DOROTHY_LABS, DOROTHY_NOTES, DOROTHY_ORDERS, DOROTHY_CHART_DOCS,
} from './data/cernerDummy';

// ─── Patient registry ────────────────────────────────────────────────────────

const CERNER_PATIENTS = {
  'CRN-PAT-00482': {
    info: MARGARET,
    vitals: CERNER_VITALS, conditions: CERNER_CONDITIONS,
    meds: CERNER_MEDICATIONS, labs: CERNER_LABS,
    notes: CERNER_NOTES, orders: CERNER_ORDERS,
    chartDocs: CERNER_CHART_DOCS,
    apptTime: '9:00 AM', status: 'In Room',
  },
  'CRN-PAT-00715': {
    info: DOROTHY,
    vitals: DOROTHY_VITALS, conditions: DOROTHY_CONDITIONS,
    meds: DOROTHY_MEDICATIONS, labs: DOROTHY_LABS,
    notes: DOROTHY_NOTES, orders: DOROTHY_ORDERS,
    chartDocs: DOROTHY_CHART_DOCS,
    apptTime: '10:30 AM', status: 'Waiting',
  },
} as const;

type PatientId = keyof typeof CERNER_PATIENTS;
const TABS = ['Summary', 'Chart Review', 'Orders', 'Results', 'Notes', 'RAAPID Coding Intel'];
const C = { navy: '#1C3A5E', mid: '#2B4C7E', accent: '#0079C1', lightBg: '#E8F4FC', text: '#7BAFD4', bg: '#F0F0F0' };

export default function CernerShell() {
  const navigate = useNavigate();
  const [selectedProvider, setSelectedProvider] = useState<MockProvider | null>(null);
  const [patientId, setPatientId] = useState<PatientId>('CRN-PAT-00482');
  const [activeTab, setActiveTab] = useState('Chart Review');
  const [rightOpen, setRightOpen] = useState(true);
  const [fhir, setFhir] = useState<FhirData | null>(null);

  useEffect(() => { fetchAllFhirData().then(setFhir); }, []);

  const handleMessage = useCallback((e: MessageEvent) => {
    if (e.origin === window.location.origin && e.data?.raapid === 'openFull') setActiveTab('RAAPID Coding Intel');
  }, []);
  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const filteredPatients = Object.entries(CERNER_PATIENTS).filter(([, p]) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return p.info.name.toLowerCase().includes(q)
      || p.info.mrn.toLowerCase().includes(q)
      || p.info.encounter.toLowerCase().includes(q);
  });

  const searchOpen = searchQuery.trim().length > 0;

  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };

  const switchPatient = (id: PatientId) => {
    setPatientId(id);
    setScheduleOpen(false);
    setSearchQuery('');
  };

  const pt = CERNER_PATIENTS[patientId];
  const { info } = pt;
  const providerSpecialty = selectedProvider?.specialty ?? 'PCP';
  const providerNameParam = encodeURIComponent(selectedProvider?.name ?? '');
  const compactUrl = `${window.location.origin}${import.meta.env.BASE_URL}compact?mode=mock&theme=cerner&patientId=${patientId}&specialty=${providerSpecialty}&providerName=${providerNameParam}`;
  const mpagesUrl  = `${window.location.origin}${import.meta.env.BASE_URL}app?mode=mock&theme=cerner&patientId=${patientId}&specialty=${providerSpecialty}&providerName=${providerNameParam}`;

  if (!selectedProvider) {
    return <CernerProviderPicker onSelect={setSelectedProvider} onBack={() => navigate('/')} />;
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'Arial, "Helvetica Neue", sans-serif', fontSize: '13px', background: C.bg, overflow: 'hidden' }}>

      {/* ── Top nav ──────────────────────────────────────────────────────── */}
      <div style={{ background: C.navy, height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px', flexShrink: 0, borderBottom: '2px solid #0F2340' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <button onClick={() => navigate('/')} style={ghost} title="Demo home"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ display: 'flex', gap: '2px' }}>
              {['#4FC3F7','#0288D1','#4FC3F7'].map((c,i) => <div key={i} style={{ width: '4px', height: i===1?'12px':'16px', marginTop: i===1?'4px':0, background: c, borderRadius: '2px' }} />)}
            </div>
            <span style={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>Cerner</span>
            <span style={{ color: C.text, fontSize: '11px', marginLeft: '3px' }}>PowerChart</span>
          </div>
          <div style={{ position: 'relative' }}>
            <div style={{ background: '#0F2340', border: `1px solid ${searchOpen ? C.accent : C.mid}`, borderRadius: '4px', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: '6px', width: '260px', transition: 'border-color 0.15s' }}>
              <span style={{ color: C.text, fontSize: '11px' }}>🔍</span>
              <input
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                placeholder="Chart Search..."
                style={{ background: 'transparent', border: 'none', outline: 'none', color: '#CBD5E1', fontSize: '11px', width: '100%' }}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', color: C.text, cursor: 'pointer', fontSize: '12px', padding: 0, lineHeight: 1 }}>&times;</button>
              )}
            </div>
            {searchOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 100, background: '#1C3A5E', border: `1px solid ${C.mid}`, borderRadius: '8px', boxShadow: '0 12px 32px rgba(0,0,0,0.5)', width: '340px', maxHeight: '290px', overflowY: 'auto' }}>
                <div style={{ padding: '6px 12px', background: '#0F2340', fontSize: '10px', color: '#4A6A8A', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', position: 'sticky', top: 0 }}>
                  Search Results · {filteredPatients.length} found
                </div>
                {filteredPatients.length === 0 ? (
                  <div style={{ padding: '20px 14px', textAlign: 'center' }}>
                    <div style={{ color: '#4A6A8A', fontSize: '12px' }}>No patients match "<span style={{ color: '#93C5FD' }}>{searchQuery}</span>"</div>
                    <div style={{ color: '#3A5A7A', fontSize: '11px', marginTop: '4px' }}>Try searching by name, MRN, or encounter type</div>
                  </div>
                ) : (
                  filteredPatients.map(([id, p]) => {
                    const active = id === patientId;
                    return (
                      <button key={id} onClick={() => switchPatient(id as PatientId)} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '10px', padding: '10px 14px', background: active ? C.accent : 'transparent', border: 'none', borderBottom: `1px solid ${C.mid}`, cursor: 'pointer', textAlign: 'left' }}
                        onMouseEnter={e => { if (!active) e.currentTarget.style.background = C.mid; }}
                        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: active ? 'rgba(255,255,255,0.2)' : C.mid, display: 'flex', alignItems: 'center', justifyContent: 'center', color: active ? 'white' : C.text, fontSize: '10px', fontWeight: 700, flexShrink: 0 }}>
                          {p.info.name.split(',')[0].slice(0, 2).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: 'white', fontSize: '12px', fontWeight: 700 }}>{p.info.name}</div>
                          <div style={{ color: active ? 'rgba(255,255,255,0.7)' : C.text, fontSize: '10px', marginTop: '1px' }}>
                            MRN: {p.info.mrn} · {p.info.age} · {p.info.encounter}
                          </div>
                        </div>
                        <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '8px', fontWeight: 700, background: p.status === 'In Room' ? '#14532D' : '#78350F', color: p.status === 'In Room' ? '#4ADE80' : '#FCD34D', flexShrink: 0 }}>{p.status}</span>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Notifications */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setNotifOpen(!notifOpen); setProfileOpen(false); }} style={{ background: notifOpen ? C.mid : 'transparent', border: 'none', color: '#4FC3F7', cursor: 'pointer', fontSize: '14px', padding: '4px 6px', borderRadius: '4px', position: 'relative' }}>
              🔔
              <span style={{ position: 'absolute', top: '0', right: '2px', width: '14px', height: '14px', borderRadius: '50%', background: '#EF4444', color: 'white', fontSize: '8px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>3</span>
            </button>
            {notifOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 100, background: '#1C3A5E', border: `1px solid ${C.mid}`, borderRadius: '8px', boxShadow: '0 12px 32px rgba(0,0,0,0.5)', width: '320px', overflow: 'hidden' }}>
                <div style={{ padding: '8px 12px', background: '#0F2340', fontSize: '11px', color: '#CBD5E1', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Notifications</span>
                  <span style={{ fontSize: '10px', color: '#4FC3F7', cursor: 'pointer' }}>Mark all read</span>
                </div>
                {[
                  { icon: '🎯', text: 'RAAPID: 3 new HCC coding gaps detected for Thompson, Margaret E.', time: '10 min ago', unread: true },
                  { icon: '⚠️', text: 'Recaptured Risk: code I25.10 flagged — evidence not found for Thompson, M.', time: '25 min ago', unread: true },
                  { icon: '✅', text: 'Code E11.65 accepted and written back to EHR successfully', time: '1 hr ago', unread: true },
                  { icon: '📊', text: 'RAF score updated: +0.665 projected lift for Williams, Dorothy', time: '2 hrs ago', unread: false },
                ].map((n, i) => (
                  <div key={i} style={{ padding: '10px 12px', borderBottom: `1px solid ${C.mid}`, display: 'flex', gap: '10px', alignItems: 'flex-start', background: n.unread ? 'rgba(0,121,193,0.1)' : 'transparent', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#0F2340'}
                    onMouseLeave={e => e.currentTarget.style.background = n.unread ? 'rgba(0,121,193,0.1)' : 'transparent'}
                  >
                    <span style={{ fontSize: '14px', flexShrink: 0, marginTop: '1px' }}>{n.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '11px', color: '#CBD5E1', lineHeight: 1.4 }}>{n.text}</div>
                      <div style={{ fontSize: '10px', color: '#4A6A8A', marginTop: '2px' }}>{n.time}</div>
                    </div>
                    {n.unread && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: C.accent, flexShrink: 0, marginTop: '4px' }} />}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Profile */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setProfileOpen(!profileOpen); setNotifOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: profileOpen ? C.mid : 'transparent', border: 'none', cursor: 'pointer', padding: '3px 8px', borderRadius: '6px' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '10px', fontWeight: 700 }}>{selectedProvider.initials}</div>
              <span style={{ color: '#CBD5E1', fontSize: '12px' }}>{selectedProvider.name}</span>
              <span style={{ color: C.text, fontSize: '10px' }}>{profileOpen ? '▲' : '▼'}</span>
            </button>
            {profileOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 100, background: '#1C3A5E', border: `1px solid ${C.mid}`, borderRadius: '8px', boxShadow: '0 12px 32px rgba(0,0,0,0.5)', width: '220px', overflow: 'hidden' }}>
                <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.mid}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '13px', fontWeight: 700 }}>{selectedProvider.initials}</div>
                  <div>
                    <div style={{ color: 'white', fontSize: '12px', fontWeight: 700 }}>{selectedProvider.name}</div>
                    <div style={{ color: '#4A6A8A', fontSize: '10px' }}>{selectedProvider.title}</div>
                  </div>
                </div>
                {[
                  { label: 'My Profile', icon: '👤' },
                  { label: 'Preferences', icon: '⚙️' },
                  { label: 'Help & Support', icon: '❓' },
                ].map((item) => (
                  <div key={item.label} style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '8px', color: '#CBD5E1', fontSize: '12px', cursor: 'pointer', borderBottom: `1px solid ${C.mid}` }}
                    onMouseEnter={e => e.currentTarget.style.background = '#0F2340'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: '13px' }}>{item.icon}</span>{item.label}
                  </div>
                ))}
                <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '8px', color: '#EF4444', fontSize: '12px', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#0F2340'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  onClick={() => setSelectedProvider(null)}
                >
                  <span style={{ fontSize: '13px' }}>🚪</span>Sign Out
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Today's Schedule dropdown ─────────────────────────────────────── */}
      <div style={{ background: '#0F2340', borderBottom: `1px solid ${C.navy}`, padding: '5px 14px', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, position: 'relative' }}>
        <span style={{ color: '#4A6A8A', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>Schedule</span>
        <button onClick={() => setScheduleOpen(!scheduleOpen)} style={{ background: '#1C3A5E', border: `1px solid ${C.mid}`, borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', flex: 1, maxWidth: '420px' }}>
          <div style={{ textAlign: 'left', flex: 1 }}>
            <div style={{ color: 'white', fontSize: '12px', fontWeight: 700 }}>{pt.info.name}</div>
            <div style={{ color: C.text, fontSize: '10px' }}>{pt.info.encounter} · {pt.apptTime}</div>
          </div>
          <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '8px', fontWeight: 700, background: pt.status === 'In Room' ? '#14532D' : '#78350F', color: pt.status === 'In Room' ? '#4ADE80' : '#FCD34D', flexShrink: 0 }}>{pt.status}</span>
          <span style={{ color: C.text, fontSize: '10px' }}>{scheduleOpen ? '▲' : '▼'}</span>
        </button>
        {/* Dropdown */}
        {scheduleOpen && (
          <div style={{ position: 'absolute', top: '100%', left: '14px', zIndex: 50, background: '#1C3A5E', border: `1px solid ${C.mid}`, borderRadius: '8px', overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.4)', minWidth: '380px' }}>
            <div style={{ padding: '6px 12px', background: '#0F2340', fontSize: '10px', color: '#4A6A8A', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Today's Patient Schedule</div>
            {Object.entries(CERNER_PATIENTS).map(([id, p]) => {
              const active = id === patientId;
              return (
                <button key={id} onClick={() => switchPatient(id as PatientId)} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '12px', padding: '10px 14px', background: active ? C.accent : 'transparent', border: 'none', borderBottom: `1px solid ${C.mid}`, cursor: 'pointer', textAlign: 'left' }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = C.mid; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ color: 'white', fontSize: '13px', fontWeight: 700 }}>{p.info.name}</div>
                    <div style={{ color: active ? 'rgba(255,255,255,0.7)' : C.text, fontSize: '11px', marginTop: '2px' }}>
                      {p.info.age} · {p.info.encounter} · MRN: {p.info.mrn}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ color: active ? 'rgba(255,255,255,0.8)' : '#93C5FD', fontSize: '11px', fontWeight: 600 }}>{p.apptTime}</div>
                    <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '8px', fontWeight: 700, background: p.status === 'In Room' ? '#14532D' : '#78350F', color: p.status === 'In Room' ? '#4ADE80' : '#FCD34D' }}>{p.status}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Patient bar ──────────────────────────────────────────────────── */}
      <div style={{ background: C.mid, padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, borderBottom: `1px solid ${C.navy}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
          <div>
            <span style={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>{info.name}</span>
            <span style={{ color: '#93C5FD', fontSize: '12px', marginLeft: '10px' }}>{info.gender} · {info.age} · DOB: {info.dob} · MRN: {info.mrn}</span>
          </div>
          <Sep />
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#4ADE80', display: 'inline-block' }} />
            <span style={{ color: '#93C5FD', fontSize: '12px' }}>{info.encounter} · {info.date}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={{ background: '#1B3A6B', border: '1px solid #4FC3F7', color: '#4FC3F7', padding: '2px 10px', borderRadius: '12px', fontSize: '10px', fontWeight: 700 }}>{info.plan}</span>
          {fhir?.source === 'fhir' && <span style={{ background: '#052E16', border: '1px solid #4ADE80', color: '#4ADE80', padding: '2px 8px', borderRadius: '12px', fontSize: '9px', fontWeight: 700 }}>● Live FHIR</span>}
        </div>
      </div>

      {/* ── Main layout ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Left sidebar */}
        <div style={{ width: '52px', background: C.navy, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '8px', gap: '2px', flexShrink: 0, borderRight: '1px solid #0F2340' }}>
          {([['📋','Chart','Chart Review'],['📊','Results','Results'],['💊','Meds','Summary'],['⚕️','Orders','Orders'],['📝','Notes','Notes'],['🩺','Problems','Summary']] as const).map(([icon, label, tab]) => {
            const active = activeTab === tab;
            return (
              <button key={label} onClick={() => setActiveTab(tab as string)} style={{ background: active ? C.mid : 'transparent', border: 'none', color: active ? '#93C5FD' : C.text, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px', padding: '7px 2px', borderRadius: '4px', width: '48px', fontSize: '16px' }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = C.mid; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                title={label}
              ><span>{icon}</span><span style={{ fontSize: '8px', color: active ? '#93C5FD' : C.text }}>{label}</span></button>
            );
          })}
        </div>

        {/* Center content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Tab bar */}
          <div style={{ display: 'flex', background: C.mid, borderBottom: '2px solid #0F2340', flexShrink: 0, padding: '0 6px' }}>
            {TABS.map((tab) => {
              const active = activeTab === tab;
              return (
                <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '9px 16px', border: 'none', background: active ? C.bg : 'transparent', color: active ? C.navy : '#93C5FD', fontWeight: active ? 700 : 400, fontSize: '12px', cursor: 'pointer', borderRadius: '4px 4px 0 0', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '4px', fontFamily: 'Arial, sans-serif' }}>
                  {tab}
                </button>
              );
            })}
          </div>

          {/* Content — key forces iframe remount on patient switch */}
          <div style={{ flex: 1, overflow: 'hidden' }} key={patientId}>
            {activeTab === 'RAAPID Coding Intel' ? (
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ background: '#E8EEF5', borderBottom: '1px solid #C5D3E0', padding: '4px 12px', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <span style={{ background: C.accent, color: 'white', fontSize: '9px', padding: '1px 6px', borderRadius: '8px', fontWeight: 700 }}>ACTIVE</span>
                </div>
                <iframe src={mpagesUrl} style={{ flex: 1, height: 0, minHeight: 0, border: 'none', width: '100%' }} title="RAAPID Coding Intelligence" allow="clipboard-write" />
              </div>
            ) : activeTab === 'Summary' ? (
              <SummaryTab pt={pt} fhir={fhir} />
            ) : activeTab === 'Chart Review' ? (
              <ChartReviewTab docs={pt.chartDocs} notes={pt.notes} />
            ) : activeTab === 'Orders' ? (
              <OrdersTab orders={pt.orders} />
            ) : activeTab === 'Results' ? (
              <ResultsTab labs={pt.labs} />
            ) : activeTab === 'Notes' ? (
              <NotesTab notes={pt.notes} />
            ) : null}
          </div>
        </div>

        {/* Right RAAPID panel — key forces iframe remount on patient switch */}
        <div style={{ width: rightOpen ? '360px' : '32px', flexShrink: 0, borderLeft: '2px solid #0F2340', display: 'flex', flexDirection: 'column', background: C.navy, transition: 'width 0.2s ease', overflow: 'hidden' }}>
          <div style={{ background: C.accent, padding: '8px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            {rightOpen && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <img src={`${import.meta.env.BASE_URL}raapid-logo.png`} alt="RAAPID" style={{ height: '14px', filter: 'brightness(0) invert(1)' }} />
                <span style={{ color: 'white', fontSize: '11px', fontWeight: 600, opacity: 0.9 }}>Coding Intelligence</span>
              </div>
            )}
            <button onClick={() => setRightOpen(!rightOpen)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontSize: '12px', marginLeft: rightOpen ? 0 : 'auto' }}>{rightOpen ? '→' : '←'}</button>
          </div>
          {rightOpen && <iframe key={patientId} src={compactUrl} style={{ flex: 1, height: 0, minHeight: 0, border: 'none', width: '100%', background: '#F9FAFB' }} title="RAAPID Summary" allow="clipboard-write" />}
        </div>
      </div>

      {/* Status bar */}
      <div style={{ background: C.navy, borderTop: '1px solid #0F2340', padding: '2px 14px', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
        <span style={{ color: '#4A6A8A', fontSize: '10px' }}>Cerner PowerChart™ · Demo Environment</span>
        <span style={{ color: '#4A6A8A', fontSize: '10px' }}>Session: {new Date().toLocaleTimeString()} · {selectedProvider.name} · {selectedProvider.title}</span>
      </div>
    </div>
  );
}

// ─── Tab content ──────────────────────────────────────────────────────────────

function SummaryTab({ pt, fhir }: { pt: typeof CERNER_PATIENTS[PatientId]; fhir: FhirData | null }) {
  const conditions = fhir?.conditions?.length ? fhir.conditions : pt.conditions;
  const meds = fhir?.medications?.length ? fhir.medications : pt.meds;
  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', alignContent: 'start' }}>
      {fhir?.source && (
        <div style={{ gridColumn: '1/-1', fontSize: '10px', color: fhir.source === 'fhir' ? '#059669' : '#9CA3AF', background: fhir.source === 'fhir' ? '#ECFDF5' : '#F9FAFB', border: `1px solid ${fhir.source === 'fhir' ? '#A7F3D0' : '#E5E7EB'}`, borderRadius: '6px', padding: '4px 10px' }}>
          {fhir.source === 'fhir' ? '● Data from Cerner open FHIR sandbox' : '○ Showing demo data (Cerner FHIR unavailable)'}
        </div>
      )}
      <Card title="Latest Vitals" sub={pt.vitals[0].date}>
        <VR label="Blood Pressure" value={pt.vitals[0].bp} alert={parseInt(pt.vitals[0].bp) > 140} />
        <VR label="Heart Rate" value={pt.vitals[0].hr} />
        <VR label="SpO₂" value={pt.vitals[0].spo2} alert={parseInt(pt.vitals[0].spo2) < 95} />
        <VR label="Weight / BMI" value={`${pt.vitals[0].wt} / BMI ${pt.vitals[0].bmi}`} alert={parseFloat(pt.vitals[0].bmi) < 19 || parseFloat(pt.vitals[0].bmi) > 30} />
        <VR label="Temp / RR" value={`${pt.vitals[0].temp} / ${pt.vitals[0].rr}`} />
      </Card>
      <Card title="Active Conditions" sub={`${conditions.length} problems`}>
        {conditions.slice(0,6).map((c) => (
          <div key={c.icd} style={{ padding: '5px 0', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '12px', color: '#111827', flex: 1 }}>{c.display}</span>
            <code style={{ fontSize: '10px', color: '#6B7280', background: '#F3F4F6', padding: '1px 4px', borderRadius: '3px', marginLeft: '6px', flexShrink: 0 }}>{c.icd}</code>
          </div>
        ))}
      </Card>
      <Card title="Current Medications" sub={`${meds.length} active`}>
        {meds.slice(0,5).map((m, i) => (
          <div key={i} style={{ padding: '5px 0', borderBottom: '1px solid #F3F4F6' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#111827' }}>{'name' in m ? m.name : ''}</div>
            <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '1px' }}>{'sig' in m ? m.sig : ''}</div>
          </div>
        ))}
      </Card>
      <Card title="Encounter" sub={pt.info.date}>
        <VR label="Type" value={pt.info.encounter} /><VR label="Physician" value={pt.info.physician} /><VR label="Insurance" value={pt.info.plan} />
      </Card>
    </div>
  );
}

const DOC_CATEGORY_STYLE: Record<string, { bg: string; color: string; icon: string }> = {
  Lab:       { bg: '#EFF6FF', color: '#1D4ED8', icon: '🔬' },
  Imaging:   { bg: '#F0FDF4', color: '#15803D', icon: '📷' },
  Consult:   { bg: '#FFF7ED', color: '#C2410C', icon: '📋' },
  Procedure: { bg: '#FAF5FF', color: '#7E22CE', icon: '🩺' },
  ED:        { bg: '#FEF2F2', color: '#DC2626', icon: '🚨' },
  Discharge: { bg: '#FFFBEB', color: '#B45309', icon: '🏥' },
  Note:      { bg: '#F0F4F8', color: '#1C3A5E', icon: '📝' },
};

type ChartDoc = { date: string; type: string; category: string; provider: string; excerpt: string };

function parseDocDate(d: string) {
  const [m, dd, y] = d.split('/');
  return y + m + dd;
}

type NoteEntry = { date: string; type: string; provider: string; excerpt: string };

function ChartReviewTab({ docs, notes }: { docs: readonly ChartDoc[]; notes?: readonly NoteEntry[] }) {
  const allDocs: ChartDoc[] = [
    ...docs,
    ...(notes || []).map(n => ({ ...n, category: 'Note' })),
  ].sort((a, b) => parseDocDate(b.date).localeCompare(parseDocDate(a.date)));
  const [selectedIdx, setSelectedIdx] = useState(0);
  const selected = allDocs[selectedIdx];
  const cat = selected ? (DOC_CATEGORY_STYLE[selected.category] || DOC_CATEGORY_STYLE.Lab) : DOC_CATEGORY_STYLE.Lab;
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '14px', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <h3 style={{ fontSize: '13px', fontWeight: 700, color: C.navy, margin: 0 }}>Chart Review</h3>
        <span style={{ fontSize: '10px', color: '#9CA3AF' }}>{allDocs.length} documents</span>
      </div>
      <select
        value={selectedIdx}
        onChange={e => setSelectedIdx(Number(e.target.value))}
        style={{ width: '100%', padding: '8px 10px', fontSize: '12px', border: '1px solid #E5E7EB', borderRadius: '6px', background: 'white', color: C.navy, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
      >
        {allDocs.map((d, i) => (
          <option key={i} value={i}>[{d.category}] {d.date} — {d.type}</option>
        ))}
      </select>
      {selected && (
        <div style={{ flex: 1, overflow: 'auto', background: 'white', border: '1px solid #E5E7EB', borderRadius: '6px' }}>
          <div style={{ background: '#F0F4F8', padding: '10px 14px', borderBottom: '2px solid #C5D3E0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '9px', padding: '2px 8px', borderRadius: '8px', fontWeight: 700, background: cat.bg, color: cat.color }}>{selected.category}</span>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: C.navy }}>{selected.type}</div>
                <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>{selected.provider}</div>
              </div>
            </div>
            <span style={{ fontSize: '12px', color: '#6B7280', fontFamily: 'monospace' }}>{selected.date}</span>
          </div>
          <div style={{ padding: '14px', fontSize: '12px', color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap', fontFamily: "'Courier New', Courier, monospace" }}>{selected.excerpt}</div>
        </div>
      )}
    </div>
  );
}

function OrdersTab({ orders }: { orders: typeof CERNER_ORDERS }) {
  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '14px' }}>
      <h3 style={{ fontSize: '13px', fontWeight: 700, color: C.navy, marginBottom: '10px', borderBottom: '2px solid #E5E7EB', paddingBottom: '6px' }}>Orders</h3>
      <Table headers={['Order','Type','Status','Date','Provider']}>
        {orders.map((o, i) => (
          <tr key={i} style={{ background: i%2?'#FAFAFA':'white', borderBottom: '1px solid #F3F4F6' }}>
            <td style={{ padding: '8px 10px', fontWeight: 600, color: '#111827' }}>{o.name}</td>
            <td style={{ padding: '8px 10px', color: '#6B7280' }}>{o.type}</td>
            <td style={{ padding: '8px 10px' }}><StatusChip s={o.status} /></td>
            <td style={{ padding: '8px 10px', color: '#6B7280', fontFamily: 'monospace', fontSize: '11px' }}>{o.date}</td>
            <td style={{ padding: '8px 10px', color: '#6B7280' }}>{o.provider}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function ResultsTab({ labs }: { labs: typeof CERNER_LABS }) {
  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '14px' }}>
      <h3 style={{ fontSize: '13px', fontWeight: 700, color: C.navy, marginBottom: '10px', borderBottom: '2px solid #E5E7EB', paddingBottom: '6px' }}>Lab Results</h3>
      <Table headers={['Test','Result','Reference','Flag','Date','Note']}>
        {labs.map((lab, i) => (
          <tr key={i} style={{ background: lab.flag?'#FFF7ED':i%2?'#FAFAFA':'white', borderBottom: '1px solid #F3F4F6' }}>
            <td style={{ padding: '7px 10px', fontWeight: 600, color: '#111827' }}>{lab.name}</td>
            <td style={{ padding: '7px 10px', fontWeight: 700, color: lab.flag==='H'?'#B45309':lab.flag==='L'?'#1D4ED8':'#111827' }}>{lab.value}</td>
            <td style={{ padding: '7px 10px', color: '#9CA3AF', fontSize: '11px' }}>{lab.ref}</td>
            <td style={{ padding: '7px 10px' }}>{lab.flag && <span style={{ padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 700, background: lab.flag==='H'?'#FEF3C7':'#DBEAFE', color: lab.flag==='H'?'#B45309':'#1D4ED8' }}>{lab.flag}</span>}</td>
            <td style={{ padding: '7px 10px', color: '#6B7280', fontFamily: 'monospace', fontSize: '11px' }}>{lab.date}</td>
            <td style={{ padding: '7px 10px', color: '#9CA3AF', fontSize: '11px', fontStyle: 'italic' }}>{lab.note}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function NotesTab({ notes }: { notes: typeof CERNER_NOTES }) {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const selected = notes[selectedIdx];
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '14px', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <h3 style={{ fontSize: '13px', fontWeight: 700, color: C.navy, margin: 0 }}>Clinical Notes</h3>
        <span style={{ fontSize: '10px', color: '#9CA3AF' }}>{notes.length} notes</span>
      </div>
      <select
        value={selectedIdx}
        onChange={e => setSelectedIdx(Number(e.target.value))}
        style={{ width: '100%', padding: '8px 10px', fontSize: '12px', border: '1px solid #E5E7EB', borderRadius: '6px', background: 'white', color: C.navy, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
      >
        {notes.map((n, i) => (
          <option key={i} value={i}>{n.date} — {n.type}</option>
        ))}
      </select>
      {selected && (
        <div style={{ flex: 1, overflow: 'auto', background: 'white', border: '1px solid #E5E7EB', borderLeft: `4px solid ${C.accent}`, borderRadius: '0 6px 6px 0' }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '13px', color: C.navy }}>{selected.type}</div>
              <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>{selected.provider}</div>
            </div>
            <span style={{ fontSize: '12px', color: '#9CA3AF', fontFamily: 'monospace' }}>{selected.date}</span>
          </div>
          <div style={{ padding: '14px', fontSize: '13px', color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{selected.excerpt}</div>
        </div>
      )}
    </div>
  );
}

// ─── Shared primitives ────────────────────────────────────────────────────────

function Card({ title, sub, children }: { title: string; sub: string; children: React.ReactNode }) {
  return (
    <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '6px', overflow: 'hidden' }}>
      <div style={{ background: '#F0F4F8', padding: '7px 12px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, color: C.navy }}>{title}</span>
        <span style={{ fontSize: '10px', color: '#9CA3AF' }}>{sub}</span>
      </div>
      <div style={{ padding: '8px 12px' }}>{children}</div>
    </div>
  );
}

function VR({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #F9FAFB' }}>
      <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{label}</span>
      <span style={{ fontSize: '12px', fontWeight: 600, color: alert ? '#B45309' : '#111827' }}>{value}</span>
    </div>
  );
}

function Table({ headers, children }: { headers: string[]; children: React.ReactNode }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
      <thead><tr style={{ background: '#F0F4F8' }}>{headers.map(h => <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 700, color: C.navy, borderBottom: '2px solid #C5D3E0', fontSize: '11px' }}>{h}</th>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  );
}

function StatusChip({ s }: { s: string }) {
  const bg = s==='Completed'?'#ECFDF5':s==='Pending'?'#FFFBEB':'#EFF6FF';
  const color = s==='Completed'?'#059669':s==='Pending'?'#B45309':'#1D4ED8';
  return <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 700, background: bg, color }}>{s}</span>;
}

function Sep() { return <span style={{ width: '1px', height: '14px', background: '#2A4F7C' }} />; }
const ghost: React.CSSProperties = { background: 'transparent', border: 'none', color: '#7BAFD4', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px', fontSize: '14px', display: 'flex', alignItems: 'center' };

function CernerProviderPicker({ onSelect, onBack }: { onSelect: (p: MockProvider) => void; onBack: () => void }) {
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'Arial, "Helvetica Neue", sans-serif', background: C.bg }}>
      <div style={{ background: C.navy, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '2px solid #0F2340' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.text, cursor: 'pointer', display: 'flex', alignItems: 'center' }} title="Back to home"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: 'white', fontSize: '12px', fontWeight: 900 }}>C</span>
          </div>
          <span style={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>Cerner PowerChart</span>
        </div>
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
        <div style={{ width: '100%', maxWidth: '480px' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <span style={{ color: 'white', fontSize: '22px', fontWeight: 900 }}>C</span>
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>Select Provider</h2>
            <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>Choose your provider profile to begin this session</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {MOCK_PROVIDERS.map((p) => (
              <button
                key={p.initials}
                onClick={() => onSelect(p)}
                style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', background: 'white', border: '1px solid #E5E7EB', borderRadius: '10px', cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.15s, box-shadow 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.boxShadow = `0 0 0 3px ${C.accent}20`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px', fontWeight: 700, flexShrink: 0 }}>{p.initials}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>{p.name}</div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{p.title}</div>
                </div>
                <span style={{ color: '#D1D5DB', fontSize: '18px' }}>→</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
